import { EditorState, LexicalEditor, $getRoot, $createParagraphNode } from "lexical";
import RichTextEditor, { RichTextEditorProps } from "./RichTextEditor";
import { initEditorWithHtml, handleEditorChangeToHtml } from "@/lib/htmlHelpers";
import { useEffect, useState, useRef } from "react";

interface HtmlRichTextEditorProps extends Omit<RichTextEditorProps, "onChange"> {
  onChange?: (htmlContent: string) => void;
  initialContent?: string;
}

/**
 * A Rich Text Editor component that works with HTML format for input/output.
 * This wraps the Lexical-based RichTextEditor but handles conversion to/from HTML.
 */
export default function HtmlRichTextEditor({ onChange, initialContent, ...props }: HtmlRichTextEditorProps) {
  const [editorInstance, setEditorInstance] = useState<LexicalEditor | null>(null);
  const lastProcessedInitialContentRef = useRef<string | null | undefined>(undefined);
  const latestInternalHtmlRef = useRef<string | null | undefined>(undefined); // Stores HTML from the editor's own onChange

  // Effect to load/update content when initialContent prop changes or editor instance is set
  useEffect(() => {
    const editor = editorInstance;
    if (!editor) {
      return; // Editor not ready
    }

    // If the incoming initialContent is identical to what we last processed, do nothing.
    if (initialContent === lastProcessedInitialContentRef.current) {
      // console.log(`[HtmlRichTextEditor useEffect] initialContent is identical to last processed value. Skipping.`); // Can be noisy
      return;
    }

    // If initialContent matches the HTML that was just set internally by this editor's onChange,
    // it means this update is an echo of our own change. Avoid re-initializing.
    // However, do update lastProcessedInitialContentRef so we know this content has been "seen".
    if (initialContent === latestInternalHtmlRef.current) {
      // console.log("[HtmlRichTextEditor useEffect] initialContent matches latest internal HTML. Skipping re-init."); // Can be noisy
      lastProcessedInitialContentRef.current = initialContent;
      return;
    }
    if (initialContent && typeof initialContent === "string" && initialContent.trim() !== "") {
      try {
        // Better detection of HTML content - must have opening and closing tags
        const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(initialContent);
        let contentToPassToEditor: string;

        if (!hasHtmlTags) {
          // console.log("[HtmlRichTextEditor useEffect] Content treated as PLAIN TEXT. Formatting it..."); // Can be noisy
          const formattedText = initialContent
            .split(/\n\n+/)
            .map((paragraph) => {
              if (paragraph.includes("\n- ")) {
                const listItems = paragraph.split("\n- ");
                const heading = listItems.shift();
                return `<p dir="ltr" style="text-align: start;"><span style="white-space: pre-wrap;">${
                  heading || ""
                }</span></p><ul>${listItems
                  .map((item) => `<li><span style="white-space: pre-wrap;">${item.trim()}</span></li>`)
                  .join("")}</ul>`;
              }
              const lines = paragraph.split("\n");
              if (lines.length > 1) {
                return `<p dir="ltr" style="text-align: start;"><span style="white-space: pre-wrap;">${lines.join(
                  "<br>"
                )}</span></p>`;
              }
              return `<p dir="ltr" style="text-align: start;"><span style="white-space: pre-wrap;">${paragraph}</span></p>`;
            })
            .join("");
          contentToPassToEditor = formattedText;
        } else {
          // console.log("[HtmlRichTextEditor useEffect] Content treated as HTML. Using existing HTML content."); // Can be noisy
          contentToPassToEditor = initialContent;
        }
        initEditorWithHtml(editor, contentToPassToEditor);
        lastProcessedInitialContentRef.current = initialContent;
      } catch (error) {
        console.error("[HtmlRichTextEditor useEffect] Error initializing editor with HTML:", error);
        editor.update(() => {
          const root = $getRoot();
          if (root.isEmpty()) {
            const paragraph = $createParagraphNode();
            root.append(paragraph);
          }
        });
        lastProcessedInitialContentRef.current = initialContent;
      }
    } else {
      // console.log("[HtmlRichTextEditor useEffect] initialContent is empty or invalid. Clearing editor."); // Can be noisy
      editor.update(() => {
        $getRoot().clear();
        const paragraph = $createParagraphNode();
        $getRoot().append(paragraph);
      });
      lastProcessedInitialContentRef.current = initialContent;
    }
  }, [initialContent, editorInstance]);

  const handleChange = (editorState: EditorState, _editor: LexicalEditor, _tags: Set<string>) => {
    if (onChange) {
      handleEditorChangeToHtml(editorState, (htmlString) => {
        latestInternalHtmlRef.current = htmlString; // Store the HTML produced by the editor
        onChange(htmlString); // Propagate to parent
      });
    }
  };

  return (
    <RichTextEditor
      {...props}
      initialContent={undefined}
      onInitialState={(editorFromCallback) => {
        // console.log("[HtmlRichTextEditor onInitialState] Editor instance received. Deferring setState."); // Can be noisy
        requestAnimationFrame(() => {
          setEditorInstance(editorFromCallback);
        });
      }}
      onChange={handleChange}
    />
  );
}
